import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Camera, MapPin, Loader2, X } from "lucide-react";
import { toast } from "sonner";

interface Props {
  gigId: string;
  onSubmitted?: () => void;
}

export const ProofUploader = ({ gigId, onSubmitted }: Props) => {
  const { user } = useAuth();
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const captureGps = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Location captured");
      },
      () => toast.error("Could not get location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async () => {
    if (!user) return;
    if (!note.trim() && files.length === 0) return toast.error("Add a note or photo");
    setBusy(true);
    try {
      const urls: string[] = [];
      for (const f of files) {
        const path = `${user.id}/${gigId}/${Date.now()}-${f.name.replace(/\s+/g, "_")}`;
        const { error: upErr } = await supabase.storage.from("gig-evidence").upload(path, f, { upsert: false });
        if (upErr) throw upErr;
        urls.push(path);
      }
      const { error } = await supabase.rpc("submit_gig_proof", {
        _gig_id: gigId,
        _note: note.trim() || null,
        _media_urls: urls,
        _lat: coords?.lat ?? null,
        _lng: coords?.lng ?? null,
      });
      if (error) throw error;
      toast.success("Proof submitted — awaiting approval");
      setNote(""); setFiles([]); setCoords(null);
      onSubmitted?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
      <p className="text-sm font-semibold">Submit completion proof</p>
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What did you do? Any notes for the poster…"
        rows={3}
        maxLength={1000}
      />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 5))}
      />
      <div className="flex gap-2 flex-wrap">
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
          <Camera className="h-4 w-4 mr-1" /> Add photos {files.length ? `(${files.length})` : ""}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={captureGps}>
          <MapPin className="h-4 w-4 mr-1" /> {coords ? "GPS ✓" : "Capture GPS"}
        </Button>
      </div>
      {files.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {files.map((f, i) => (
            <span key={i} className="text-[10px] bg-muted px-2 py-1 rounded-full flex items-center gap-1">
              {f.name.slice(0, 20)}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Button onClick={submit} disabled={busy} className="w-full rounded-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit proof"}
      </Button>
    </div>
  );
};
